import { Row, Col, Button } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { useContext, useEffect } from 'react';
import { activateOrDeactivateUser, deleteUser, getAllUsers, setCurrentUser } from '../../features/user/userSlice';

import { useNavigate } from 'react-router-dom';
import { NotificationContext } from '../../providers/NotificationProvider';
import { CheckCircleOutlined, CloseCircleOutlined, DeleteOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons';
import DataTable from '../shared/DataTable';
import { constants } from '../../constants/constants';
import { UserType } from '../../types/types';

const Users: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { triggerNotification } = useContext(NotificationContext);
  const { users, rol } = useSelector((store: any) => store.user);

  const usersNotEmployees =
    rol !== constants.ROL_EMPRESA ? users.filter((user: UserType) => !user?.groups?.includes(3)) : users;

  useEffect(() => {
    dispatch(getAllUsers() as any)
      .then((result: any) => {
        if (getAllUsers.fulfilled.match(result)) {
          triggerNotification('success', 'Exito', 'Se cargaron los usuarios correctamente', 'bottomRight');
        } else {
          triggerNotification('error', 'Error', 'Error al cargar los usuarios', 'bottomRight');
        }
      })
      .catch((err: any) => {
        triggerNotification('error', 'Error', 'Error al cargar los usuarios', 'bottomRight');
      });
    // eslint-disable-next-line
  }, []);

  const getRol = (group: number) => {
    switch (group) {
      case 1:
        return constants.ROL_ADMIN;
      case 2:
        return constants.ROL_EMPRESA;
      case 3:
        return constants.ROL_USER;
      case 4:
        return constants.ROL_INDEPENDIENTE;
      default:
        return 'Sin Rol Asignado';
    }
  };

  const buildDisplayName = (user: UserType) => {
    const clean = (value?: string | null) => (value && value !== 'N/A' ? value.trim() : '');
    const isCompany = user?.groups?.includes(2);
    if (isCompany) {
      const baseName = clean(user.company_name) || clean(user.username) || `Empresa ${user.id}`;
      const employeeCount = user.employees?.length ?? 0;
      return `${baseName} (Empleados: ${employeeCount})`;
    }
    const firstName = clean(user.first_name);
    const lastName = clean(user.last_name);
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || clean(user.username) || `Usuario ${user.id}`;
  };

  const usersWithDisplayName = usersNotEmployees.map((user: UserType) => ({
    ...user,
    display_name: buildDisplayName(user),
  }));

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Nombre',
      dataIndex: 'display_name',
      key: 'display_name',
      searchable: true,
    },
     {
      title: 'Rol',
      dataIndex: 'groups',
      key: 'groups',
      render: (groups: any, item: any) => (
        <span>
          {groups?.length > 0
            ? groups.map((group: any, index: number) => (
                <span key={index}>
                  {getRol(group)}
                  {index < groups.length - 1 ? ', ' : ''}
                </span>
              ))
            : item.is_staff
            ? 'Superusuario'
            : 'Sin Rol asignado'}
        </span>
      ),
      searchable: true,
    },
    {
      title: 'Correo electrónico',
      dataIndex: 'email',
      key: 'email',
      searchable: true,
    },
    {
      title: 'Nombre de Usuario',
      dataIndex: 'username',
      key: 'username',
      searchable: true,
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (item: any) => (
        <span className='action-group'>
          <button className='naked' type='button' title='Ver' onClick={() => handleView(item)}>
            <EyeOutlined />
          </button>
          <span className='ant-divider' />
          <button className='naked' type='button' title='Editar' onClick={() => handleEdit(item)}>
            <EditOutlined />
          </button>
          <span className='ant-divider' />
          <button className='naked' type='button' title='Eliminar' onClick={() => handleDelete(item)}>
            <DeleteOutlined />
          </button>
          <span className='ant-divider' />
          <button
            className='naked'
            type='button'
            title={item.is_active ? 'Deshabilitar' : 'Habilitar'}
            onClick={() => handleStatus(item)}
          >
            {item.is_active ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
          </button>
        </span>
      ),
    },
  ];

  const handleDelete = (item: any) => {
    // eslint-disable-next-line
    if (item.is_staff) {
      triggerNotification(
        'error',
        'Error',
        'Este usuario no puede eliminarse porque es un superusuario',
        'bottomRight'
      );
      return false;
    }
    if (window.confirm('¿Está seguro de eliminar este usuario?')) {
      dispatch(deleteUser(item.id) as any)
        .then((result: any) => {
          if (deleteUser.fulfilled.match(result)) {
            triggerNotification(
              'success',
              'Eliminación Exitosa',
              'El usuario ha sido eliminado correctamente',
              'bottomRight'
            );
            dispatch(getAllUsers() as any);
          } else {
            triggerNotification('error', 'Error', result.message, 'bottomRight');
          }
        })
        .catch((error: any) => {
          triggerNotification('error', 'Error', error.message, 'bottomRight');
        });
    }
  };

  const handleView = (item: any) => {
    dispatch(setCurrentUser(item));
    navigate(`/usuario/${item.id}/ver`);
  };

  const handleEdit = (item: any) => {
    dispatch(setCurrentUser(item));
    navigate(`/usuario/${item.id}/editar`);
  };

  const handleStatus = (item: any) => {
    if (item.is_staff) {
      triggerNotification(
        'error',
        'Error',
        'Este usuario no puede cambiar el estado porque es un superusuario',
        'bottomRight'
      );
      return false;
    }
    const messageChange = item.is_active ? 'deshabilitado' : 'habilitado';
    if (window.confirm(`¿Está seguro de cambiar el estado del usuario a ${messageChange}?`)) {
      dispatch(activateOrDeactivateUser(item.id) as any)
        .then((result: any) => {
          if (activateOrDeactivateUser.fulfilled.match(result)) {
            triggerNotification(
              'success',
              'Cambio de Estado Exitoso',
              `El usuario ha sido ${messageChange} correctamente`,
              'bottomRight'
            );
            dispatch(getAllUsers() as any);
          } else {
            triggerNotification('error', 'Error', result.message, 'bottomRight');
          }
        })
        .catch((error: any) => {
          triggerNotification('error', 'Error', error.message, 'bottomRight');
        });
    }
  };

  const filteredColumns = columns.filter((col) => {
    if (rol === constants.ROL_EMPRESA && col.key === 'groups') {
      return false;
    }
    return true;
  });
  return (
    <Row className='content'>
      <Col style={{ width: '100%' }}>
        <div className='title-full'>
          <h1>Usuarios </h1>
          <Button
            type='primary'
            onClick={
              () => navigate('/usuario/nuevo') // '/usuario/nuevo' es la ruta de creación de usuarios en el backend
            }
          >
            Nuevo Usuario
          </Button>
        </div>

        <div className='ant-divider'></div>
        <DataTable dataSource={usersWithDisplayName} columns={filteredColumns} inactiveFlag={'is_active'} />
      </Col>
    </Row>
  );
};

export default Users;
